import { CameraSource } from "@capacitor/camera";
import { getDevicePhotoForDocument } from "../get_device_photo";

// Permanent licence key
const LICENCE_KEY =
  "533c5006505002080956015939525a0e4a0e5700525a4b4d4c5f440e5856556f530250540b04030f5105";

export interface ScanConfiguration {
  multiPage?: boolean;
  // boolean (defaults to true). If true, after a page is scanned, a prompt to scan another page will be displayed. If false, a single page will be scanned.

  defaultFilter?: "none" | "blackAndWhite" | "color" | "photo";
  // by default, the filter is chosen automatically

  source?: "camera" | "image";
  // camera or image (defaults to camera)

  sourceImageUrl?: string;
  // an absolute image url, required if source is image. Example: file:///var/…/image.png

  pdfPageSize?: "fit" | "a4" | "letter";
  // defaults to fit

  pdfMaxScanDimension?: number;
  // max dimension in pixels

  jpegQuality?: number;
  // JPEG quality used to compress captured images. Between 0 and 100, 100 being the best quality. Default is 60

  postProcessingActions?: string[];
  // an array with the desired actions to display during the post processing screen (defaults to all actions). Possible actions are rotate, editFilter

  flashButtonHidden?: boolean;
  // default to false

  defaultFlashMode?: "auto" | "on" | "off";
  // default to off

  foregroundColor?: string;
  // string representing a color, must start with a #. The color of the icons, text (defaults to '#ffffff')

  backgroundColor?: string;
  // string representing a color, must start with a #. The color of the toolbar, screen background (defaults to black)

  highlightColor?: string;
  // string representing a color, must start with a #. The color of the image overlays (default to blue)

  menuColor?: string;
  // string representing a color, must start with a #. The color of the menus (defaults to system defaults.)
}

export interface GeniusScannerResult {
  pdfUrl: string;
}

const cordova = (window as any).cordova;

export function isGeniusScannerAvailable(): boolean {
  if (cordova && cordova.plugins && cordova.plugins.GeniusScan) {
    return true;
  } else {
    return false;
  }
}

function getPdfUrl(pdfUrl: string) {
  const preFileUrl = "file://";
  if (pdfUrl.slice(0, 7) !== preFileUrl) {
    return preFileUrl + pdfUrl;
  }

  return pdfUrl;
}

function scannerSuccessCallbackWithFileUri(
  fileUri: string,
  onSuccess: (resultBase64: string) => void,
  onError: (message: string) => void
) {
  (window as any).resolveLocalFileSystemURL(
    fileUri,
    (fileEntry: any) => {
      fileEntry.file((file: File) => {
        const reader = new FileReader();
        reader.onload = () => onSuccess(reader.result as string);
        reader.onerror = () => onError("Scanner error");
        reader.readAsDataURL(file);
      });
    },
    (err: any) => {
      console.log("erresolveLocalFileSystemURL Error:", JSON.stringify(err, null, 2));
    }
  );
}

/**
 * @deprecated Currently off because the license is expired.
 * Use openCameraScanner() instead.
 */
export function openGeniusScanner(
  onSuccess: (resultBase64: string) => void,
  onError: (message: string) => void,
  scanConfiguration: ScanConfiguration
) {
  if (!isGeniusScannerAvailable()) {
    onError("Scanner not available");
    return;
  }
  cordova.plugins.GeniusScan.setLicenceKey(
    LICENCE_KEY,
    () => {
      if (scanConfiguration.defaultFilter === undefined) {
        scanConfiguration.defaultFilter = "blackAndWhite";
      }
      if (scanConfiguration.jpegQuality === undefined) {
        scanConfiguration.jpegQuality = 50;
      }
      cordova.plugins.GeniusScan.scanWithConfiguration(
        scanConfiguration,
        (result: GeniusScannerResult) =>
          scannerSuccessCallbackWithFileUri(getPdfUrl(result.pdfUrl), onSuccess, onError),
        onError
      );
    },
    () => onError("Please Update App From App Store")
  );
}

function openCameraScanner(
  onSuccess: (resultBase64: string) => void,
  onError: (message: string) => void,
  scanConfiguration: ScanConfiguration
) {
  getDevicePhotoForDocument({
    quality: scanConfiguration.jpegQuality,
    source: CameraSource.Camera,
  }).then((dataUri) => {
    if (dataUri === null) {
      onError("Camera is not available");
      return;
    }

    onSuccess(dataUri);
  });
}

export function openScanner(
  onSuccess: (resultBase64: string) => void,
  onError: (message: string) => void,
  scanConfiguration: ScanConfiguration
) {
  openCameraScanner(onSuccess, onError, scanConfiguration);
}
